/**
 * This file contains utility functions that are used in both
 *
 * transform-itinerary-to-calculator-form-data and
 * transform-route-option-to-calculator-form-data
 *
 * to transform the data to the emissions calculator form data.
 */

import {
  EMISSIONS_CALC_AIR_DEFAULT_CARGO_ITEMS,
  EMISSIONS_CALC_FORM_FIELD_DEFAULTS,
} from '@/components/modules/emissions/calculator/constants';
import {
  convertLocationToPlaceFormat,
  convertModeToVehicleType,
} from '@/components/modules/emissions/calculator/form/utils';
import { DISTANCE_UNITS, MODE_WATER, VEHICLE_TYPES } from '@/lib/constants';
import { LocationDictionaryModel, LocationModel } from '@/lib/models/location/types';
import {
  CargoOption,
  EmissionsSegmentInterface,
  EmissionsVehicles,
  LocationInterface,
  RouteViaInterface,
} from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { extractVehicleIdentifier } from '@/utils/vehicle';

/**
 * Helper to create consistent segment objects for emissions calculator
 */
export const createEmissionsSegment = ({
  vehicleType,
  vehicleId,
  flightNumber,
  distance,
  origin,
  stopOffs,
}: {
  vehicleType: EmissionsVehicles | null;
  vehicleId: string;
  flightNumber: string;
  distance: number;
  origin?: LocationInterface;
  stopOffs: LocationInterface[];
}): EmissionsSegmentInterface => ({
  vehicle: { type: vehicleType, vehicleId, flightNumber },
  distance,
  distanceUnit: DISTANCE_UNITS.KM,
  origin,
  stopOffs,
});

/**
 * Helper to extract vehicle ID consistently from different data sources
 */
export const extractVehicleFromSegment = (vehicleId: string, mode: SegmentModeType): string => {
  const vehicleType = convertModeToVehicleType(mode);
  return extractVehicleIdentifier(vehicleId, vehicleType === VEHICLE_TYPES.VESSEL ? 'vesselImo' : 'iata')?.id || '';
};

/**
 * Helper to process stop-offs consistently for both itinerary and option data
 */
export const processStopOffs = (
  stopIds: string[] | RouteViaInterface[],
  locations: LocationModel[] | LocationDictionaryModel,
  isViaData: boolean = false,
): LocationInterface[] => {
  const stopOffs = stopIds
    .map((item) => {
      const locationId = isViaData ? (item as RouteViaInterface).locationId : (item as string);
      const location = isViaData
        ? (locations as LocationDictionaryModel)[locationId]
        : (locations as LocationModel[]).find((loc) => loc.id === locationId);
      return location ? convertLocationToPlaceFormat(location) : null;
    })
    .filter((location): location is NonNullable<typeof location> => location !== null);

  return stopOffs;
};

/**
 * Helper to create final destination segment
 */
export const createFinalDestinationSegment = (
  destinationLocation: LocationModel | LocationDictionaryModel[keyof LocationDictionaryModel],
): EmissionsSegmentInterface => ({
  vehicle: { type: null, vehicleId: '', flightNumber: '' },
  distance: 0,
  distanceUnit: DISTANCE_UNITS.KM,
  origin: convertLocationToPlaceFormat(destinationLocation),
  stopOffs: [],
});

export const getCargoItemsForMode = (mode: SegmentModeType, cargoParams?: CargoOption) => {
  const defaultItems =
    mode === MODE_WATER ? EMISSIONS_CALC_FORM_FIELD_DEFAULTS.cargoItems : EMISSIONS_CALC_AIR_DEFAULT_CARGO_ITEMS;

  if (!cargoParams) {
    return defaultItems;
  }

  return defaultItems.map((item) => {
    const updatedItem = { ...item };

    // Replace containerSize if it exists in cargoParams
    if (cargoParams.containerSize) {
      updatedItem.containerSize = cargoParams.containerSize;
    }

    // Replace weight if it exists in cargoParams
    if (cargoParams.weight?.value) {
      updatedItem.totalWeight = cargoParams.weight.value;
    }

    return updatedItem;
  });
};
